import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { ClusterContent } from '../network/ContentLoader';
import { axialToWorld } from '../math/HexMath';

import vertSrc from '../shaders/ring.vert.glsl?raw';
import fragSrc from '../shaders/ring.frag.glsl?raw';

const FLOATS_PER_INSTANCE = 8;
const RING_SEGMENTS = 64;

const TYPE_COLORS: Record<string, readonly [number, number, number]> = {
  terran: [0.75, 0.7, 0.6],
  desert: [0.85, 0.65, 0.4],
  ice:    [0.8, 0.9, 1.0],
  lava:   [0.5, 0.25, 0.1],
};

export class RingRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _config: StellarRenderConfig;
  private _hexRadius: number;
  private _ringCount = 0;
  private _instanceBuffer: Float32Array;

  constructor(
    gl: WebGL2RenderingContext,
    config: StellarRenderConfig,
    hexRadius: number,
    maxInstances: number,
  ) {
    this._config = config;
    this._hexRadius = hexRadius;
    this._instanceBuffer = new Float32Array(maxInstances * FLOATS_PER_INSTANCE);
    this._shader = new ShaderProgram(gl, vertSrc, fragSrc);

    const geo = createRingGeometry(RING_SEGMENTS);
    this._mesh = new InstancedMesh(
      gl, this._shader, geo.vertices,
      [{ name: 'a_position', size: 2, divisor: 0 }],
      [
        { name: 'a_worldPos', size: 3, divisor: 1 },
        { name: 'a_ringScale', size: 1, divisor: 1 },
        { name: 'a_ringColor', size: 3, divisor: 1 },
        { name: 'a_ringTilt', size: 1, divisor: 1 },
      ],
      geo.indices,
    );
  }

  updateFromContent(contents: ClusterContent[]): void {
    let count = 0;
    const max = this._instanceBuffer.length / FLOATS_PER_INSTANCE;
    for (let c = 0; c < contents.length; c++) {
      const planets = contents[c].planets;
      for (let i = 0; i < planets.length; i++) {
        if (count >= max) break;
        const p = planets[i];
        if (!p.has_rings) continue;
        if (p.axial_q == null || p.axial_r == null) continue;

        const [wx, wz] = axialToWorld(p.axial_q, p.axial_r, this._hexRadius);
        const size = (p.scale ?? 1.0) * this._config.planetBaseSize;
        const seed = ((p.seed ?? 0) % 65536) / 65536.0;
        const color = TYPE_COLORS[p.planet_type ?? 'terran'] ?? TYPE_COLORS.terran;
        const tilt = seed * Math.PI * 2.0;

        const idx = count * FLOATS_PER_INSTANCE;
        this._instanceBuffer[idx] = wx;
        this._instanceBuffer[idx + 1] = size;
        this._instanceBuffer[idx + 2] = wz;
        this._instanceBuffer[idx + 3] = size * (1.8 + seed * 0.7);
        this._instanceBuffer[idx + 4] = color[0];
        this._instanceBuffer[idx + 5] = color[1];
        this._instanceBuffer[idx + 6] = color[2];
        this._instanceBuffer[idx + 7] = tilt;
        count++;
      }
    }
    this._ringCount = count;
    this._mesh.updateInstances(
      this._instanceBuffer.subarray(0, count * FLOATS_PER_INSTANCE), count,
    );
  }

  get ringCount(): number { return this._ringCount; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._ringCount === 0) return;

    gl.depthMask(false);

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);

    this._mesh.draw();

    gl.depthMask(true);
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
  }
}

function createRingGeometry(segments: number): {
  vertices: Float32Array; indices: Uint16Array;
} {
  const verts: number[] = [];
  const idxs: number[] = [];
  const innerR = 0.6;
  const outerR = 1.0;

  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2.0;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    verts.push(cos * innerR, sin * innerR);
    verts.push(cos * outerR, sin * outerR);
  }

  for (let i = 0; i < segments; i++) {
    const a = i * 2;
    const b = a + 1;
    const c = a + 2;
    const d = a + 3;
    idxs.push(a, b, c, c, b, d);
  }

  return {
    vertices: new Float32Array(verts),
    indices: new Uint16Array(idxs),
  };
}
